import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import {
  ChartBar,
  ChatsCircle,
  GearSix,
  Leaf,
  CalendarCheck,
  PlugsConnected,
  ArrowSquareOut,
  ArrowsClockwise,
  Copy,
  SignIn,
  SignOut,
  EnvelopeSimple,
  Check,
  Checks,
  Tray,
  ClockCounterClockwise,
  PaperPlaneTilt,
  Trash,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";

dayjs.locale("fr");
axios.defaults.withCredentials = true;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_ai-client-assist/artifacts/5xamlug5_Logo%20VMLH-TCC%20V2.png";

const CHANNEL_LABEL = { whatsapp: "WhatsApp", email: "E-mail" };
const channelLabel = (c) => CHANNEL_LABEL[c] || "Site web";

const StatCard = ({ label, value, testId }) => (
  <div data-testid={testId} className="rounded-2xl border border-[#E2DFD8] bg-white p-6">
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">{label}</p>
    <p className="font-serif-display mt-3 text-5xl font-light text-[#1E2A22]">{value ?? "—"}</p>
  </div>
);

const Overview = ({ stats }) => (
  <div className="space-y-6">
    <h2 className="font-serif-display text-4xl font-semibold tracking-tight text-[#1E2A22]">
      Aperçu
    </h2>
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
      <StatCard label="Conversations" value={stats?.total_conversations} testId="stat-conversations" />
      <StatCard label="Messages échangés" value={stats?.total_messages} testId="stat-messages" />
      <StatCard label="Propositions de RDV" value={stats?.slots_proposed} testId="stat-slots" />
      <StatCard label="Actives aujourd'hui" value={stats?.conversations_today} testId="stat-today" />
    </div>
  </div>
);

const Conversations = ({ initialSelected }) => {
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(initialSelected || null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const detailScrollRef = useRef(null);

  const loadList = useCallback(() => {
    axios.get(`${API}/admin/conversations`).then((r) => setConvs(r.data)).catch(() => {});
  }, []);

  const loadDetail = useCallback((sid) => {
    if (!sid) return;
    axios.get(`${API}/admin/conversations/${sid}`).then((r) => setDetail(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    setDetail(null);
    setHistory(null);
    setShowHistory(false);
    loadDetail(selected);
    if (!selected) return;
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") loadDetail(selected);
    }, 5000);
    return () => clearInterval(iv);
  }, [selected, loadDetail]);

  useEffect(() => {
    detailScrollRef.current?.scrollTo({ top: detailScrollRef.current.scrollHeight });
  }, [detail?.messages?.length]);

  const isHuman = detail?.mode === "human";

  const toggleMode = async () => {
    if (!selected) return;
    setSwitching(true);
    try {
      const action = isHuman ? "release" : "takeover";
      await axios.post(`${API}/admin/conversations/${selected}/${action}`);
      toast.success(isHuman ? "Chloé reprend la conversation." : "Vous avez la main : le patient est prévenu.");
      loadDetail(selected);
    } catch {
      toast.error("Action impossible.");
    } finally {
      setSwitching(false);
    }
  };

  const toggleHistory = async () => {
    if (showHistory) {
      setShowHistory(false);
      return;
    }
    setShowHistory(true);
    try {
      const r = await axios.get(`${API}/admin/conversations/${selected}/history`);
      setHistory(r.data);
    } catch {
      setHistory({ email: null, conversations: [] });
    }
  };

  const sendReply = async () => {
    const text = reply.trim();
    if (!text || sending || !selected) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/conversations/${selected}/reply`, { message: text });
      setReply("");
      loadDetail(selected);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="font-serif-display text-3xl font-semibold tracking-tight text-[#1E2A22] sm:text-4xl">
        Conversations
      </h2>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="max-h-[70vh] space-y-2 overflow-y-auto lg:col-span-1" data-testid="conversations-list">
          {convs.length === 0 && (
            <div className="rounded-2xl border border-dashed border-[#E2DFD8] bg-white p-8 text-center text-sm text-[#5C6B60]">
              Aucune conversation pour le moment.
            </div>
          )}
          {convs.map((c) => (
            <button
              key={c.session_id}
              data-testid="admin-conv-item"
              onClick={() => setSelected(c.session_id)}
              className={`w-full rounded-xl border p-4 text-left transition-colors ${
                selected === c.session_id
                  ? "border-[#24643D] bg-[#24643D]/5"
                  : "border-[#E2DFD8] bg-white hover:border-[#24643D]/40"
              }`}
            >
              <div className="flex items-center gap-2">
                <p className="flex-1 truncate text-sm font-medium text-[#1E2A22]">{c.preview || "—"}</p>
                {c.mode === "human" && (
                  <Badge className="shrink-0 bg-[#E07A5F] text-[10px] text-white hover:bg-[#E07A5F]">Vous</Badge>
                )}
              </div>
              <p className="mt-1 text-xs text-[#5C6B60]">
                {dayjs(c.updated_at).format("D MMM YYYY · HH[h]mm")} · {c.message_count} messages
                {c.channel && c.channel !== "web" ? ` · ${channelLabel(c.channel)}` : ""}
              </p>
            </button>
          ))}
        </div>
        <div className="flex max-h-[75vh] flex-col rounded-2xl border border-[#E2DFD8] bg-white lg:col-span-2" data-testid="conversation-detail">
          {!detail ? (
            <p className="py-16 text-center text-sm text-[#5C6B60]">
              {selected ? "Chargement…" : "Sélectionnez une conversation pour l'afficher."}
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 border-b border-[#E2DFD8] px-4 py-3 sm:px-6">
                {isHuman ? (
                  <Badge data-testid="conv-mode-badge" className="bg-[#E07A5F] text-white hover:bg-[#E07A5F]">
                    Vous avez la main
                  </Badge>
                ) : (
                  <Badge data-testid="conv-mode-badge" className="bg-[#24643D] text-white hover:bg-[#24643D]">
                    Chloé répond
                  </Badge>
                )}
                <Badge variant="outline" className="border-[#E2DFD8] text-[#5C6B60]">
                  {channelLabel(detail.channel)}
                </Badge>
                <Button
                  data-testid="patient-history-button"
                  size="sm"
                  variant="outline"
                  onClick={toggleHistory}
                  className="rounded-xl border-[#E2DFD8] text-[#5C6B60] hover:border-[#24643D]/40"
                >
                  <ClockCounterClockwise size={14} className="mr-1" /> Historique
                </Button>
                <Button
                  data-testid="takeover-button"
                  size="sm"
                  variant={isHuman ? "outline" : "default"}
                  onClick={toggleMode}
                  disabled={switching}
                  className={`ml-auto rounded-xl ${
                    isHuman
                      ? "border-[#24643D] text-[#24643D] hover:bg-[#24643D] hover:text-white"
                      : "bg-[#E07A5F] text-white hover:bg-[#c9603f]"
                  }`}
                >
                  {switching ? "…" : isHuman ? "Rendre la main à Chloé" : "Prendre la main"}
                </Button>
              </div>
              {showHistory && (
                <div data-testid="patient-history-panel" className="border-b border-[#E2DFD8] bg-[#F9F8F6] px-4 py-3 sm:px-6">
                  {!history ? (
                    <p className="text-xs text-[#5C6B60]">Recherche de l'historique…</p>
                  ) : !history.email ? (
                    <p data-testid="history-no-email" className="text-xs text-[#5C6B60]">
                      Aucune adresse e-mail identifiée dans cette conversation — impossible de relier ce patient à d'autres échanges.
                    </p>
                  ) : (
                    <>
                      <p className="text-xs font-semibold text-[#1E2A22]">
                        Historique de {history.email} · {history.conversations.length} conversation(s)
                      </p>
                      <div className="mt-2 max-h-44 space-y-1.5 overflow-y-auto">
                        {history.conversations.map((c) => (
                          <button
                            key={c.session_id}
                            data-testid="history-conv-item"
                            disabled={c.session_id === selected}
                            onClick={() => {
                              setSelected(c.session_id);
                              setShowHistory(false);
                              setHistory(null);
                            }}
                            className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs transition-colors ${
                              c.session_id === selected
                                ? "border-[#24643D]/40 bg-[#24643D]/5"
                                : "border-[#E2DFD8] bg-white hover:border-[#24643D]/40"
                            }`}
                          >
                            <span className="shrink-0 rounded bg-[#EFECE6] px-1.5 py-0.5 text-[10px] text-[#5C6B60]">
                              {channelLabel(c.channel)}
                            </span>
                            <span className="min-w-0 flex-1 truncate text-[#1E2A22]">{c.preview || "—"}</span>
                            <span className="shrink-0 text-[#5C6B60]">{dayjs(c.updated_at).format("D MMM YYYY")}</span>
                            {c.session_id === selected && (
                              <span className="shrink-0 text-[10px] font-semibold text-[#24643D]">actuelle</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
              <div ref={detailScrollRef} className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
                {detail.messages.map((m) => {
                  if (m.author === "system") {
                    return (
                      <div key={m.id} className="flex justify-center" data-testid="admin-system-notice">
                        <p className="rounded-full bg-[#1E2A22]/5 px-4 py-1.5 text-center text-xs italic text-[#5C6B60]">
                          {m.content}
                        </p>
                      </div>
                    );
                  }
                  const isTherapist = m.author === "therapist";
                  return (
                    <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[75%] rounded-xl px-4 py-3 text-sm leading-relaxed ${
                          m.role === "user"
                            ? "bg-[#24643D] text-white"
                            : isTherapist
                              ? "border border-[#E07A5F]/50 bg-[#E07A5F]/10 text-[#1E2A22]"
                              : "border border-[#E2DFD8] bg-[#F9F8F6] text-[#1E2A22]"
                        }`}
                      >
                        {isTherapist && (
                          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[#E07A5F]">
                            Vous · praticien
                          </p>
                        )}
                        <p className="whitespace-pre-wrap">{m.content.replace(/\[SLOTS(?::\d+)?\]/g, "").replace(/\[(RESCHEDULE|CANCEL):[^\]]+\]/g, "").replace(/\[RELAY\]/g, "").trim()}</p>
                        {m.has_slots && (
                          <Badge className="mt-2 bg-[#E07A5F] text-white hover:bg-[#E07A5F]">
                            <CalendarCheck size={12} className="mr-1" /> Créneaux proposés
                          </Badge>
                        )}
                        <p className={`mt-1.5 text-[10px] ${m.role === "user" ? "text-white/60" : "text-[#5C6B60]"}`}>
                          {dayjs(m.created_at).format("D MMM · HH[h]mm")}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              {isHuman && (
                <div className="flex items-end gap-2 border-t border-[#E2DFD8] p-3 sm:p-4">
                  <Textarea
                    data-testid="admin-reply-input"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendReply();
                      }
                    }}
                    rows={2}
                    placeholder="Votre réponse au patient…"
                    className="flex-1 resize-none rounded-xl border-[#E2DFD8]"
                  />
                  <Button
                    data-testid="admin-reply-send"
                    onClick={sendReply}
                    disabled={sending || !reply.trim()}
                    className="rounded-xl bg-[#24643D] text-white hover:bg-[#1B4A2E]"
                  >
                    {sending ? "…" : "Envoyer"}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const RELAY_STATUS = {
  nouveau: { label: "Nouveau", cls: "bg-[#E07A5F] text-white hover:bg-[#E07A5F]" },
  lu: { label: "Lu", cls: "bg-[#EFECE6] text-[#1E2A22] hover:bg-[#EFECE6] border border-[#E2DFD8]" },
  traite: { label: "Traité", cls: "bg-[#24643D] text-white hover:bg-[#24643D]" },
};

const RelayedMessages = ({ onCountChange, onOpenConversation }) => {
  const [relays, setRelays] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/relays`).then((r) => setRelays(r.data)).catch(() => setRelays([]));
  }, []);

  const setStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/relays/${id}`, { status });
      setRelays((rs) => {
        const next = rs.map((r) => (r.id === id ? { ...r, status } : r));
        onCountChange?.(next.filter((r) => (r.status || "nouveau") === "nouveau").length);
        return next;
      });
    } catch {
      toast.error("Impossible de mettre à jour le statut.");
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="font-serif-display text-3xl font-semibold tracking-tight text-[#1E2A22] sm:text-4xl">
        Messages transmis
      </h2>
      <p className="text-sm leading-relaxed text-[#5C6B60]">
        Messages que les patients ont demandé à Chloé de transmettre au thérapeute. Marquez-les
        « lu » puis « traité » une fois la personne recontactée.
      </p>
      <div className="space-y-4" data-testid="relays-list">
        {relays === null && <p className="text-sm text-[#5C6B60]">Chargement…</p>}
        {relays !== null && relays.length === 0 && (
          <div className="rounded-2xl border border-dashed border-[#E2DFD8] bg-white p-8 text-center text-sm text-[#5C6B60]">
            Aucun message transmis pour le moment.
          </div>
        )}
        {(relays || []).map((r) => {
          const status = RELAY_STATUS[r.status] ? r.status : "nouveau";
          return (
            <div key={r.id} data-testid="relay-item" className="rounded-2xl border border-[#E2DFD8] bg-white p-5 sm:p-6">
              <div className="flex flex-wrap items-center gap-2">
                <Badge data-testid="relay-status-badge" className={RELAY_STATUS[status].cls}>
                  {RELAY_STATUS[status].label}
                </Badge>
                <Badge variant="outline" className="border-[#E2DFD8] text-[#5C6B60]">
                  {r.channel === "whatsapp" ? "WhatsApp" : "Site web"}
                </Badge>
                {r.emailed ? (
                  <span className="text-xs text-[#24643D]">✓ e-mail envoyé</span>
                ) : (
                  <span className="text-xs text-[#E07A5F]">e-mail non envoyé (SMTP)</span>
                )}
                <span className="ml-auto text-xs text-[#5C6B60]">
                  {dayjs(r.created_at).format("D MMM YYYY · HH[h]mm")}
                </span>
              </div>
              <pre className="mt-4 max-h-56 overflow-y-auto whitespace-pre-wrap rounded-xl bg-[#F9F8F6] p-4 font-sans text-sm leading-relaxed text-[#1E2A22]">
                {r.transcript}
              </pre>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  data-testid="relay-open-conversation"
                  size="sm"
                  variant="outline"
                  onClick={() => onOpenConversation?.(r.session_id)}
                  className="rounded-xl border-[#24643D] text-[#24643D] hover:bg-[#24643D] hover:text-white"
                >
                  <ChatsCircle size={14} className="mr-1" /> Ouvrir la conversation
                </Button>
                {status === "nouveau" && (
                  <Button
                    data-testid="relay-mark-read"
                    variant="outline"
                    size="sm"
                    onClick={() => setStatus(r.id, "lu")}
                    className="rounded-xl border-[#E2DFD8] text-[#1E2A22]"
                  >
                    <Check size={14} className="mr-1" /> Marquer comme lu
                  </Button>
                )}
                {status !== "traite" ? (
                  <Button
                    data-testid="relay-mark-done"
                    size="sm"
                    onClick={() => setStatus(r.id, "traite")}
                    className="rounded-xl bg-[#24643D] text-white hover:bg-[#1d5232]"
                  >
                    <Checks size={14} className="mr-1" /> Marquer comme traité
                  </Button>
                ) : (
                  <Button
                    data-testid="relay-reopen"
                    variant="outline"
                    size="sm"
                    onClick={() => setStatus(r.id, "nouveau")}
                    className="rounded-xl border-[#E2DFD8] text-[#5C6B60]"
                  >
                    Rouvrir
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DRAFT_STATUS = {
  pending: { label: "À valider", cls: "bg-[#E07A5F] text-white hover:bg-[#E07A5F]" },
  sent: { label: "Envoyé", cls: "bg-[#24643D] text-white hover:bg-[#24643D]" },
  discarded: { label: "Ignoré", cls: "bg-[#EFECE6] text-[#5C6B60] hover:bg-[#EFECE6] border border-[#E2DFD8]" },
};

const AutoGrowTextarea = ({ value, ...props }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight + 2}px`;
    }
  }, [value]);
  return <Textarea ref={ref} value={value} {...props} />;
};

const DraftCard = ({ d, value, onChangeBody, busy, onSend, onDiscard, onOpenConv }) => (
  <div data-testid="email-draft-item" className="rounded-2xl border border-[#E2DFD8] bg-white p-5 sm:p-6">
    <div className="flex flex-wrap items-center gap-2">
      <Badge data-testid="draft-status-badge" className={DRAFT_STATUS[d.status]?.cls || DRAFT_STATUS.pending.cls}>
        {DRAFT_STATUS[d.status]?.label || d.status}
      </Badge>
      <Badge variant="outline" className="border-[#E2DFD8] text-[#5C6B60]">
        {d.classification === "SIMPLE" ? (d.auto && d.test_mode ? "Simple · retenu par le mode test" : d.auto && d.status === "sent" ? "Simple · envoyé automatiquement" : "Simple") : "Complexe"}
      </Badge>
      <span className="text-xs font-medium text-[#1E2A22]">{d.to_email}</span>
      <span className="ml-auto text-xs text-[#5C6B60]">{dayjs(d.created_at).format("D MMM YYYY · HH[h]mm")}</span>
    </div>
    <p className="mt-3 text-sm font-medium text-[#1E2A22]">Objet : {d.subject}</p>
    <details className="mt-2">
      <summary className="cursor-pointer text-xs text-[#5C6B60] hover:text-[#24643D]">Voir le message du patient</summary>
      <pre className="mt-2 whitespace-pre-wrap rounded-xl bg-[#F9F8F6] p-3 font-sans text-xs leading-relaxed text-[#1E2A22]">
        {d.original_body}
      </pre>
    </details>
    {d.status === "pending" ? (
      <>
        <label className="mt-4 block text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">
          Réponse proposée par Chloé (modifiable)
        </label>
        <AutoGrowTextarea
          data-testid="draft-body-input"
          value={value}
          onChange={(e) => onChangeBody(e.target.value)}
          rows={6}
          className="mt-1.5 min-h-[10rem] resize-none overflow-hidden rounded-xl border-[#E2DFD8] text-sm"
        />
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            data-testid="draft-send-button"
            size="sm"
            onClick={onSend}
            disabled={busy}
            className="rounded-xl bg-[#24643D] text-white hover:bg-[#1d5232]"
          >
            <PaperPlaneTilt size={14} className="mr-1" /> {busy ? "…" : "Envoyer la réponse"}
          </Button>
          <Button
            data-testid="draft-discard-button"
            size="sm"
            variant="outline"
            onClick={onDiscard}
            disabled={busy}
            className="rounded-xl border-[#E07A5F] text-[#E07A5F] hover:bg-[#E07A5F] hover:text-white"
          >
            <Trash size={14} className="mr-1" /> Ignorer
          </Button>
          <Button
            data-testid="draft-open-conversation"
            size="sm"
            variant="outline"
            onClick={onOpenConv}
            className="rounded-xl border-[#E2DFD8] text-[#5C6B60]"
          >
            <ChatsCircle size={14} className="mr-1" /> Conversation
          </Button>
        </div>
      </>
    ) : (
      <pre className="mt-3 whitespace-pre-wrap rounded-xl bg-[#F9F8F6] p-3 font-sans text-xs leading-relaxed text-[#1E2A22]">
        {d.body}
      </pre>
    )}
  </div>
);

const EmailDrafts = ({ onCountChange, onOpenConversation }) => {
  const [drafts, setDrafts] = useState(null);
  const [agent, setAgent] = useState(null);
  const [edited, setEdited] = useState({});
  const [busy, setBusy] = useState(null);
  const [sim, setSim] = useState({ from_email: "", subject: "", body: "" });
  const [simBusy, setSimBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/email/drafts`).then((r) => {
      setDrafts(r.data);
      onCountChange?.(r.data.filter((d) => d.status === "pending").length);
    }).catch(() => setDrafts([]));
    axios.get(`${API}/admin/settings`).then((r) => setAgent(r.data)).catch(() => {});
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  const sendDraft = async (d) => {
    setBusy(d.id);
    try {
      const body = edited[d.id] !== undefined ? edited[d.id] : d.body;
      const r = await axios.post(`${API}/admin/email/drafts/${d.id}/send`, { body });
      r.data.ok ? toast.success(r.data.message) : toast.error(r.data.message);
      if (r.data.ok) load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible.");
    } finally {
      setBusy(null);
    }
  };

  const discardDraft = async (d) => {
    setBusy(d.id);
    try {
      await axios.delete(`${API}/admin/email/drafts/${d.id}`);
      toast.success("Brouillon ignoré.");
      load();
    } catch {
      toast.error("Action impossible.");
    } finally {
      setBusy(null);
    }
  };

  const simulate = async () => {
    if (!sim.from_email.trim() || !sim.body.trim()) {
      toast.error("Adresse e-mail et message requis.");
      return;
    }
    setSimBusy(true);
    try {
      const r = await axios.post(`${API}/admin/email/simulate`, sim, { timeout: 120000 });
      toast.success(`Catégorie : ${r.data.category} — ${r.data.action}.`);
      setSim({ from_email: "", subject: "", body: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la simulation.");
    } finally {
      setSimBusy(false);
    }
  };

  const pending = (drafts || []).filter((d) => d.status === "pending");
  const others = (drafts || []).filter((d) => d.status !== "pending").slice(0, 20);

  const renderCard = (d) => (
    <DraftCard
      key={d.id}
      d={d}
      value={edited[d.id] !== undefined ? edited[d.id] : d.body}
      onChangeBody={(v) => setEdited((m) => ({ ...m, [d.id]: v }))}
      busy={busy === d.id}
      onSend={() => sendDraft(d)}
      onDiscard={() => discardDraft(d)}
      onOpenConv={() => onOpenConversation?.(d.session_id)}
    />
  );

  return (
    <div className="space-y-6">
      <h2 className="font-serif-display text-3xl font-semibold tracking-tight text-[#1E2A22] sm:text-4xl">
        E-mails Chloé
      </h2>
      {agent && (
        <div data-testid="email-agent-status" className="flex flex-wrap items-center gap-2">
          {agent.email_agent_enabled ? (
            <Badge className="bg-[#24643D] text-white hover:bg-[#24643D]">Agent e-mail actif</Badge>
          ) : (
            <Badge variant="outline" className="border-[#E07A5F] text-[#E07A5F]">Agent e-mail désactivé</Badge>
          )}
          {agent.email_agent_enabled && agent.email_agent_test_mode && (
            <Badge className="bg-[#E07A5F] text-white hover:bg-[#E07A5F]">Mode test — aucun envoi automatique</Badge>
          )}
          {agent.email_agent_last_poll_at && (
            <span className="text-xs text-[#5C6B60]">
              Dernière relève : {dayjs(agent.email_agent_last_poll_at).format("D MMM · HH[h]mm")}
            </span>
          )}
        </div>
      )}
      <p className="text-sm leading-relaxed text-[#5C6B60]">
        Chloé lit les e-mails adressés à son alias et prépare des réponses : les questions simples
        peuvent partir automatiquement (hors mode test), le reste attend votre validation ici. Les
        e-mails à contenu clinique ne reçoivent jamais de réponse automatique.
      </p>
      <div className="space-y-4" data-testid="email-drafts-list">
        {drafts === null && <p className="text-sm text-[#5C6B60]">Chargement…</p>}
        {drafts !== null && pending.length === 0 && (
          <div className="rounded-2xl border border-dashed border-[#E2DFD8] bg-white p-8 text-center text-sm text-[#5C6B60]">
            Aucune réponse en attente de validation.
          </div>
        )}
        {pending.map(renderCard)}
      </div>
      {others.length > 0 && (
        <>
          <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">Traités récemment</h3>
          <div className="space-y-4">{others.map(renderCard)}</div>
        </>
      )}
      <div className="rounded-2xl border border-[#E2DFD8] bg-white p-6 sm:p-8">
        <h3 className="text-lg font-semibold text-[#1E2A22]">Simuler un e-mail entrant</h3>
        <p className="mt-1 text-sm text-[#5C6B60]">
          Testez le comportement de Chloé sans envoyer de vrai e-mail : le message est traité comme
          s'il venait d'arriver dans la boîte.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            data-testid="sim-from-input"
            value={sim.from_email}
            onChange={(e) => setSim((v) => ({ ...v, from_email: e.target.value }))}
            placeholder="patient@exemple.fr"
            className="rounded-xl border-[#E2DFD8]"
          />
          <Input
            data-testid="sim-subject-input"
            value={sim.subject}
            onChange={(e) => setSim((v) => ({ ...v, subject: e.target.value }))}
            placeholder="Objet"
            className="rounded-xl border-[#E2DFD8]"
          />
        </div>
        <Textarea
          data-testid="sim-body-input"
          value={sim.body}
          onChange={(e) => setSim((v) => ({ ...v, body: e.target.value }))}
          rows={4}
          placeholder="Bonjour, quels sont vos tarifs ?…"
          className="mt-3 rounded-xl border-[#E2DFD8]"
        />
        <Button
          data-testid="sim-send-button"
          variant="outline"
          onClick={simulate}
          disabled={simBusy}
          className="mt-3 rounded-xl border-[#24643D] text-[#24643D] hover:bg-[#24643D] hover:text-white"
        >
          {simBusy ? "Traitement…" : "Simuler la réception"}
        </Button>
      </div>
    </div>
  );
};

const Settings = ({ onLogout, email }) => {
  const [settings, setSettings] = useState(null);
  const [token, setToken] = useState("");
  const [name, setName] = useState("");
  const [info, setInfo] = useState("");
  const [siteUrl, setSiteUrl] = useState("");
  const [waToken, setWaToken] = useState("");
  const [waPhoneId, setWaPhoneId] = useState("");
  const [waAppSecret, setWaAppSecret] = useState("");
  const [waBotNumber, setWaBotNumber] = useState("");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState("587");
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpFrom, setSmtpFrom] = useState("");
  const [notifyEmail, setNotifyEmail] = useState("");
  const [therapistPhone, setTherapistPhone] = useState("");
  const [typesNotes, setTypesNotes] = useState("");
  const [imapHost, setImapHost] = useState("");
  const [imapPort, setImapPort] = useState("993");
  const [imapUser, setImapUser] = useState("");
  const [imapPassword, setImapPassword] = useState("");
  const [emailAlias, setEmailAlias] = useState("");
  const [agentEnabled, setAgentEnabled] = useState(false);
  const [agentTestMode, setAgentTestMode] = useState(true);
  const [testingImap, setTestingImap] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushSupport, setPushSupport] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/settings`).then((r) => {
      setSettings(r.data);
      setName(r.data.practice_name);
      setInfo(r.data.practice_info);
      setSiteUrl(r.data.website_url || "");
      setWaPhoneId(r.data.whatsapp_phone_number_id || "");
      setWaBotNumber(r.data.whatsapp_bot_number || "");
      setSmtpHost(r.data.smtp_host || "");
      setSmtpPort(r.data.smtp_port || "587");
      setSmtpUser(r.data.smtp_user || "");
      setSmtpFrom(r.data.smtp_from || "");
      setNotifyEmail(r.data.notify_email || "");
      setTherapistPhone(r.data.therapist_phone || "");
      setTypesNotes(r.data.session_types_notes || "");
      setImapHost(r.data.imap_host || "");
      setImapPort(r.data.imap_port || "993");
      setImapUser(r.data.imap_user || "");
      setEmailAlias(r.data.email_alias || "");
      setAgentEnabled(!!r.data.email_agent_enabled);
      setAgentTestMode(r.data.email_agent_test_mode !== false);
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        practice_name: name, practice_info: info, website_url: siteUrl, whatsapp_phone_number_id: waPhoneId,
        whatsapp_bot_number: waBotNumber,
        smtp_host: smtpHost, smtp_port: smtpPort, smtp_user: smtpUser, smtp_from: smtpFrom,
        notify_email: notifyEmail, therapist_phone: therapistPhone, session_types_notes: typesNotes,
        imap_host: imapHost, imap_port: imapPort, imap_user: imapUser, email_alias: emailAlias,
        email_agent_enabled: agentEnabled, email_agent_test_mode: agentTestMode,
      };
      if (token.trim()) payload.calendly_token = token.trim();
      if (waToken.trim()) payload.whatsapp_access_token = waToken.trim();
      if (waAppSecret.trim()) payload.whatsapp_app_secret = waAppSecret.trim();
      if (smtpPassword.trim()) payload.smtp_password = smtpPassword.trim();
      if (imapPassword.trim()) payload.imap_password = imapPassword.trim();
      const r = await axios.put(`${API}/admin/settings`, payload);
      setSettings(r.data);
      setToken("");
      setWaToken("");
      setWaAppSecret("");
      setSmtpPassword("");
      setImapPassword("");
      toast.success("Paramètres enregistrés.");
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const syncWebsite = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/admin/website/sync`, { url: siteUrl }, { timeout: 120000 });
      setInfo(r.data.practice_info);
      toast.success(`Site analysé (${r.data.pages_analyzed} pages). Informations mises à jour.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la synchronisation du site.");
    } finally {
      setSyncing(false);
    }
  };

  const testCalendly = async () => {
    setTesting(true);
    try {
      const r = await axios.post(`${API}/admin/calendly/test`);
      r.data.ok ? toast.success(r.data.message) : toast.error(r.data.message);
    } finally {
      setTesting(false);
    }
  };

  const testSmtp = async () => {
    setTestingSmtp(true);
    try {
      const r = await axios.post(`${API}/admin/smtp/test`);
      r.data.ok ? toast.success(r.data.message) : toast.error(r.data.message);
    } catch {
      toast.error("Erreur lors du test SMTP.");
    } finally {
      setTestingSmtp(false);
    }
  };

  const testImap = async () => {
    setTestingImap(true);
    try {
      const r = await axios.post(`${API}/admin/email/imap-test`, {}, { timeout: 60000 });
      r.data.ok ? toast.success(r.data.message) : toast.error(r.data.message);
    } catch {
      toast.error("Erreur lors du test IMAP.");
    } finally {
      setTestingImap(false);
    }
  };

  const sendSummaryNow = async () => {
    setSendingSummary(true);
    try {
      const r = await axios.post(`${API}/admin/summary/send`, {}, { timeout: 120000 });
      r.data.ok ? toast.success(r.data.message) : toast.error(r.data.message);
      load();
    } catch {
      toast.error("Erreur lors de l'envoi du résumé.");
    } finally {
      setSendingSummary(false);
    }
  };

  useEffect(() => {
    const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setPushSupport(supported);
    if (!supported) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setPushEnabled(!!sub))
      .catch(() => {});
  }, []);

  const urlB64ToUint8 = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
  };

  const togglePush = async () => {
    if (!pushSupport) {
      toast.error("Notifications non disponibles dans ce navigateur.");
      return;
    }
    setPushBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      if (pushEnabled && existing) {
        await axios.post(`${API}/admin/push/unsubscribe`, { endpoint: existing.endpoint });
        await existing.unsubscribe();
        setPushEnabled(false);
        toast.success("Notifications désactivées sur cet appareil.");
      } else {
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          toast.error("Autorisation refusée — activez les notifications dans les réglages.");
          return;
        }
        const { data } = await axios.get(`${API}/admin/push/vapid-key`);
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlB64ToUint8(data.key),
        });
        const json = sub.toJSON();
        await axios.post(`${API}/admin/push/subscribe`, { endpoint: json.endpoint, keys: json.keys });
        setPushEnabled(true);
        toast.success("Notifications activées sur cet appareil ✓");
      }
    } catch (e) {
      toast.error("Impossible d'activer les notifications sur cet appareil.");
    } finally {
      setPushBusy(false);
    }
  };

  const testPush = async () => {
    try {
      const r = await axios.post(`${API}/admin/push/test`);
      r.data.ok ? toast.success(r.data.message) : toast.error(r.data.message);
    } catch {
      toast.error("Erreur lors du test.");
    }
  };

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copié.`));
  };

  const appOrigin = window.location.origin;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const embedSnippet = `<script src="${appOrigin}/embed.js" async></script>`;

  return (
    <div className="max-w-2xl space-y-6">
      <h2 className="font-serif-display text-4xl font-semibold tracking-tight text-[#1E2A22]">
        Paramètres
      </h2>

      <div className="rounded-2xl border border-[#E2DFD8] bg-white p-8">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#1E2A22]">Intégration Calendly</h3>
          {settings?.calendly_configured ? (
            <Badge data-testid="calendly-status" className="bg-[#24643D] text-white hover:bg-[#24643D]">
              Connecté {settings.calendly_token_hint}
            </Badge>
          ) : (
            <Badge data-testid="calendly-status" variant="outline" className="border-[#E07A5F] text-[#E07A5F]">
              Non configuré — créneaux simulés
            </Badge>
          )}
        </div>
        <p className="mt-2 text-sm leading-relaxed text-[#5C6B60]">
          Collez votre jeton d'accès personnel Calendly pour que Chloé propose vos vrais créneaux.
          <a
            href="https://calendly.com/integrations/api_webhooks"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-1 inline-flex items-center gap-1 text-[#24643D] underline"
          >
            Obtenir un jeton <ArrowSquareOut size={12} />
          </a>
        </p>
        <div className="mt-4 flex gap-3">
          <Input
            data-testid="calendly-token-input"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="eyJraWQiOi…"
            className="rounded-xl border-[#E2DFD8]"
          />
          <Button
            data-testid="test-calendly-button"
            variant="outline"
            onClick={testCalendly}
            disabled={testing}
            className="rounded-xl border-[#24643D] text-[#24643D] hover:bg-[#24643D] hover:text-white"
          >
            <PlugsConnected size={16} className="mr-1" />
            {testing ? "Test…" : "Tester"}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-[#E2DFD8] bg-white p-8">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#1E2A22]">Site internet du cabinet</h3>
          {settings?.last_synced_at && (
            <span data-testid="last-synced" className="text-xs text-[#5C6B60]">
              Dernière synchro : {dayjs(settings.last_synced_at).format("D MMM YYYY · HH[h]mm")}
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-[#5C6B60]">
          Chloé analyse votre site pour connaître vos prestations, tarifs, adresse et modalités.
        </p>
        <div className="mt-4 flex gap-3">
          <Input
            data-testid="website-url-input"
            value={siteUrl}
            onChange={(e) => setSiteUrl(e.target.value)}
            placeholder="https://www.alesia-therapie.fr"
            className="rounded-xl border-[#E2DFD8]"
          />
          <Button
            data-testid="sync-website-button"
            variant="outline"
            onClick={syncWebsite}
            disabled={syncing || !siteUrl.trim()}
            className="shrink-0 rounded-xl border-[#24643D] text-[#24643D] hover:bg-[#24643D] hover:text-white"
          >
            <ArrowsClockwise size={16} className={`mr-1 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Analyse…" : "Synchroniser"}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-[#E2DFD8] bg-white p-8">
        <h3 className="text-lg font-semibold text-[#1E2A22]">Widget pour votre site internet</h3>
        <p className="mt-1 text-sm leading-relaxed text-[#5C6B60]">
          Collez ce code dans Squarespace : Paramètres → Avancé → Injection de code → Pied de page.
          Une bulle de chat verte apparaîtra sur toutes les pages de votre site.
        </p>
        <div className="mt-4 flex items-center gap-3">
          <code data-testid="embed-snippet" className="flex-1 overflow-x-auto rounded-xl border border-[#E2DFD8] bg-[#F9F8F6] px-4 py-3 text-xs text-[#1E2A22]">
            {embedSnippet}
          </code>
          <Button
            data-testid="copy-embed-button"
            variant="outline"
            onClick={() => copyText(embedSnippet, "Code d'intégration")}
            className="shrink-0 rounded-xl border-[#24643D] text-[#24643D] hover:bg-[#24643D] hover:text-white"
          >
            <Copy size={16} className="mr-1" /> Copier
          </Button>
        </div>
        <a
          href="/widget"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="preview-widget-link"
          className="mt-3 inline-flex items-center gap-1 text-sm text-[#24643D] underline"
        >
          Prévisualiser le widget <ArrowSquareOut size={14} />
        </a>
      </div>

      <div className="rounded-2xl border border-[#E2DFD8] bg-white p-8">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#1E2A22]">WhatsApp Business (API Meta)</h3>
          {settings?.whatsapp_configured ? (
            <Badge data-testid="whatsapp-status" className="bg-[#24643D] text-white hover:bg-[#24643D]">
              Configuré
            </Badge>
          ) : (
            <Badge data-testid="whatsapp-status" variant="outline" className="border-[#E07A5F] text-[#E07A5F]">
              Non configuré
            </Badge>
          )}
        </div>
        <p className="mt-2 text-sm leading-relaxed text-[#5C6B60]">
          1. Créez une app sur{" "}
          <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="text-[#24643D] underline">
            developers.facebook.com
          </a>{" "}
          avec le produit WhatsApp. 2. Dans « API Setup », copiez le jeton d'accès et l'ID du numéro
          de téléphone. 3. Dans « Configuration » → Webhooks, collez l'URL et le jeton de
          vérification ci-dessous, puis abonnez-vous au champ « messages ».
        </p>
        <div className="mt-5 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">
              URL du webhook (à coller chez Meta)
            </label>
            <div className="mt-1.5 flex items-center gap-3">
              <code className="flex-1 overflow-x-auto rounded-xl border border-[#E2DFD8] bg-[#F9F8F6] px-4 py-3 text-xs text-[#1E2A22]">
                {backendUrl}/api/whatsapp/webhook
              </code>
              <Button
                data-testid="copy-webhook-button"
                variant="outline"
                onClick={() => copyText(`${backendUrl}/api/whatsapp/webhook`, "URL du webhook")}
                className="shrink-0 rounded-xl border-[#E2DFD8]"
              >
                <Copy size={16} />
              </Button>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">
              Jeton de vérification (à coller chez Meta)
            </label>
            <div className="mt-1.5 flex items-center gap-3">
              <code data-testid="verify-token" className="flex-1 overflow-x-auto rounded-xl border border-[#E2DFD8] bg-[#F9F8F6] px-4 py-3 text-xs text-[#1E2A22]">
                {settings?.whatsapp_verify_token || "…"}
              </code>
              <Button
                data-testid="copy-verify-button"
                variant="outline"
                onClick={() => copyText(settings?.whatsapp_verify_token || "", "Jeton de vérification")}
                className="shrink-0 rounded-xl border-[#E2DFD8]"
              >
                <Copy size={16} />
              </Button>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">
              Jeton d'accès Meta (Access Token)
            </label>
            <Input
              data-testid="whatsapp-token-input"
              type="password"
              value={waToken}
              onChange={(e) => setWaToken(e.target.value)}
              placeholder="EAAG…"
              className="mt-1.5 rounded-xl border-[#E2DFD8]"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">
              ID du numéro de téléphone (Phone Number ID)
            </label>
            <Input
              data-testid="whatsapp-phoneid-input"
              value={waPhoneId}
              onChange={(e) => setWaPhoneId(e.target.value)}
              placeholder="123456789012345"
              className="mt-1.5 rounded-xl border-[#E2DFD8]"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">
              Clé secrète de l'app Meta (App Secret){" "}
              {settings?.whatsapp_app_secret_configured && (
                <span className="normal-case tracking-normal text-[#24643D]">— configurée ✓</span>
              )}
            </label>
            <Input
              data-testid="whatsapp-appsecret-input"
              type="password"
              value={waAppSecret}
              onChange={(e) => setWaAppSecret(e.target.value)}
              placeholder="Dashboard Meta → Paramètres de l'app → Général → Clé secrète"
              className="mt-1.5 rounded-xl border-[#E2DFD8]"
            />
            <p className="mt-1 text-xs text-[#5C6B60]">
              Requise si l'option « Exiger une preuve de clé secrète » est activée dans votre app Meta
              (erreur « appsecret_proof »).
            </p>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">
              Numéro WhatsApp de Chloé (le numéro que les patients contactent)
            </label>
            <Input
              data-testid="wa-bot-number-input"
              value={waBotNumber}
              onChange={(e) => setWaBotNumber(e.target.value)}
              placeholder="+33 6 12 34 56 78"
              className="mt-1.5 rounded-xl border-[#E2DFD8]"
            />
            <p className="mt-2 text-xs leading-relaxed text-[#5C6B60]">
              Ce numéro n'est jamais affiché : le lien court{" "}
              <code data-testid="contact-short-link" className="rounded bg-[#F9F8F6] px-1.5 py-0.5 text-[#24643D]">
                {backendUrl}/wa
              </code>{" "}
              ouvre directement la conversation WhatsApp avec Chloé. Utilisable sur votre site, vos supports…
              et partagé par Chloé aux patients qui préfèrent WhatsApp.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-[#E2DFD8] bg-white p-8">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#1E2A22]">Relais e-mail au thérapeute (SMTP)</h3>
          {settings?.smtp_configured ? (
            <Badge data-testid="smtp-status" className="bg-[#24643D] text-white hover:bg-[#24643D]">
              Configuré
            </Badge>
          ) : (
            <Badge data-testid="smtp-status" variant="outline" className="border-[#E07A5F] text-[#E07A5F]">
              Non configuré
            </Badge>
          )}
        </div>
        <p className="mt-2 text-sm leading-relaxed text-[#5C6B60]">
          Permet à Chloé de transmettre les messages des patients par e-mail et d'envoyer le résumé
          hebdomadaire. Utilisez le serveur SMTP de votre messagerie (ex. Gmail : smtp.gmail.com,
          port 587, avec un « mot de passe d'application »).
        </p>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">Serveur SMTP</label>
            <Input
              data-testid="smtp-host-input"
              value={smtpHost}
              onChange={(e) => setSmtpHost(e.target.value)}
              placeholder="smtp.gmail.com"
              className="mt-1.5 rounded-xl border-[#E2DFD8]"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">Port (587 ou 465)</label>
            <Input
              data-testid="smtp-port-input"
              value={smtpPort}
              onChange={(e) => setSmtpPort(e.target.value)}
              placeholder="587"
              className="mt-1.5 rounded-xl border-[#E2DFD8]"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">Utilisateur SMTP</label>
            <Input
              data-testid="smtp-user-input"
              value={smtpUser}
              onChange={(e) => setSmtpUser(e.target.value)}
              placeholder="adresse@votre-domaine.fr"
              className="mt-1.5 rounded-xl border-[#E2DFD8]"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">
              Mot de passe SMTP{" "}
              {settings?.smtp_password_configured && (
                <span className="normal-case tracking-normal text-[#24643D]">— configuré ✓</span>
              )}
            </label>
            <Input
              data-testid="smtp-password-input"
              type="password"
              value={smtpPassword}
              onChange={(e) => setSmtpPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-1.5 rounded-xl border-[#E2DFD8]"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">Adresse expéditrice (facultatif)</label>
            <Input
              data-testid="smtp-from-input"
              value={smtpFrom}
              onChange={(e) => setSmtpFrom(e.target.value)}
              placeholder="chloe@vmlh-tcc.fr"
              className="mt-1.5 rounded-xl border-[#E2DFD8]"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">E-mail du thérapeute (destinataire)</label>
            <Input
              data-testid="notify-email-input"
              value={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.value)}
              placeholder="victor.loher@vmlh-tcc.fr"
              className="mt-1.5 rounded-xl border-[#E2DFD8]"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">
              Téléphone du cabinet (communiqué aux patients souhaitant un contact direct)
            </label>
            <Input
              data-testid="therapist-phone-input"
              value={therapistPhone}
              onChange={(e) => setTherapistPhone(e.target.value)}
              placeholder="07 88 51 66 64"
              className="mt-1.5 rounded-xl border-[#E2DFD8]"
            />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Button
            data-testid="test-smtp-button"
            variant="outline"
            onClick={testSmtp}
            disabled={testingSmtp}
            className="rounded-xl border-[#24643D] text-[#24643D] hover:bg-[#24643D] hover:text-white"
          >
            <PlugsConnected size={16} className="mr-1" />
            {testingSmtp ? "Envoi…" : "Envoyer un e-mail de test"}
          </Button>
          <span className="text-xs text-[#5C6B60]">Enregistrez d'abord les paramètres ci-dessous.</span>
        </div>
      </div>

      <div className="rounded-2xl border border-[#E2DFD8] bg-white p-8">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#1E2A22]">Chloé répond aux e-mails (IMAP)</h3>
          {agentEnabled ? (
            <Badge data-testid="email-agent-badge" className="bg-[#24643D] text-white hover:bg-[#24643D]">
              Actif{agentTestMode ? " · mode test" : ""}
            </Badge>
          ) : (
            <Badge data-testid="email-agent-badge" variant="outline" className="border-[#E07A5F] text-[#E07A5F]">
              Désactivé
            </Badge>
          )}
        </div>
        <p className="mt-2 text-sm leading-relaxed text-[#5C6B60]">
          Chloé relève votre boîte mail toutes les 2 minutes et traite uniquement les e-mails
          adressés à son alias (ex. chloe@vmlh-tcc.fr). Questions simples : réponse automatique.
          Demandes complexes : brouillon à valider dans l'onglet « E-mails Chloé ». Contenu
          clinique : aucune réponse, le patient attend la vôtre. Avec Gmail/Google Workspace :
          imap.gmail.com, port 993, même mot de passe d'application que le SMTP.
        </p>
        <div className="mt-5 space-y-4">
          <div className="flex items-center justify-between rounded-xl border border-[#E2DFD8] bg-[#F9F8F6] px-4 py-3">
            <div>
              <p className="text-sm font-medium text-[#1E2A22]">Activer Chloé sur les e-mails</p>
              <p className="text-xs text-[#5C6B60]">Relève automatique de la boîte configurée ci-dessous.</p>
            </div>
            <Switch
              data-testid="email-agent-enabled-switch"
              checked={agentEnabled}
              onCheckedChange={setAgentEnabled}
            />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-[#E2DFD8] bg-[#F9F8F6] px-4 py-3">
            <div>
              <p className="text-sm font-medium text-[#1E2A22]">Mode test (recommandé au lancement)</p>
              <p className="text-xs text-[#5C6B60]">
                Aucun envoi automatique : toutes les réponses, même simples, attendent votre validation.
              </p>
            </div>
            <Switch
              data-testid="email-agent-testmode-switch"
              checked={agentTestMode}
              onCheckedChange={setAgentTestMode}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">Serveur IMAP</label>
              <Input
                data-testid="imap-host-input"
                value={imapHost}
                onChange={(e) => setImapHost(e.target.value)}
                placeholder="imap.gmail.com"
                className="mt-1.5 rounded-xl border-[#E2DFD8]"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">Port (993)</label>
              <Input
                data-testid="imap-port-input"
                value={imapPort}
                onChange={(e) => setImapPort(e.target.value)}
                placeholder="993"
                className="mt-1.5 rounded-xl border-[#E2DFD8]"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">Utilisateur IMAP</label>
              <Input
                data-testid="imap-user-input"
                value={imapUser}
                onChange={(e) => setImapUser(e.target.value)}
                placeholder="victor.loher@vmlh-tcc.fr"
                className="mt-1.5 rounded-xl border-[#E2DFD8]"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">
                Mot de passe IMAP{" "}
                {settings?.imap_password_configured && (
                  <span className="normal-case tracking-normal text-[#24643D]">— configuré ✓</span>
                )}
              </label>
              <Input
                data-testid="imap-password-input"
                type="password"
                value={imapPassword}
                onChange={(e) => setImapPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 rounded-xl border-[#E2DFD8]"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">
                Alias de Chloé (seuls les e-mails adressés à cette adresse sont traités)
              </label>
              <Input
                data-testid="email-alias-input"
                value={emailAlias}
                onChange={(e) => setEmailAlias(e.target.value)}
                placeholder="chloe@vmlh-tcc.fr"
                className="mt-1.5 rounded-xl border-[#E2DFD8]"
              />
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Button
            data-testid="test-imap-button"
            variant="outline"
            onClick={testImap}
            disabled={testingImap}
            className="rounded-xl border-[#24643D] text-[#24643D] hover:bg-[#24643D] hover:text-white"
          >
            <PlugsConnected size={16} className="mr-1" />
            {testingImap ? "Test…" : "Tester la connexion IMAP"}
          </Button>
          <span className="text-xs text-[#5C6B60]">Enregistrez d'abord les paramètres.</span>
        </div>
      </div>

      <div className="rounded-2xl border border-[#E2DFD8] bg-white p-8">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#1E2A22]">Résumé hebdomadaire</h3>
          {settings?.last_summary_sent_at && (
            <span data-testid="last-summary-sent" className="text-xs text-[#5C6B60]">
              Dernier envoi : {dayjs(settings.last_summary_sent_at).format("D MMM YYYY · HH[h]mm")}
            </span>
          )}
        </div>
        <p className="mt-2 text-sm leading-relaxed text-[#5C6B60]">
          Chaque lundi à 8h (heure de Paris), Chloé envoie à{" "}
          <strong>{notifyEmail || "l'e-mail du thérapeute"}</strong> un résumé de la semaine :
          conversations, demandes de rendez-vous, messages transmis — avec les points à vérifier
          (RDV à valider sur Calendly, paiements des séances initiales). Nécessite le SMTP configuré.
        </p>
        <Button
          data-testid="send-summary-button"
          variant="outline"
          onClick={sendSummaryNow}
          disabled={sendingSummary}
          className="mt-4 rounded-xl border-[#24643D] text-[#24643D] hover:bg-[#24643D] hover:text-white"
        >
          <ArrowsClockwise size={16} className={`mr-1 ${sendingSummary ? "animate-spin" : ""}`} />
          {sendingSummary ? "Génération et envoi…" : "Envoyer le résumé maintenant"}
        </Button>
      </div>

      <div className="rounded-2xl border border-[#E2DFD8] bg-white p-8">
        <h3 className="text-lg font-semibold text-[#1E2A22]">Informations du cabinet</h3>
        <p className="mt-1 text-sm text-[#5C6B60]">
          Ces informations sont utilisées par Chloé pour répondre aux patients.
        </p>
        <div className="mt-5 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">
              Nom du cabinet
            </label>
            <Input
              data-testid="practice-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1.5 rounded-xl border-[#E2DFD8]"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">
              Informations pratiques (tarifs, horaires, adresse…)
            </label>
            <Textarea
              data-testid="practice-info-input"
              value={info}
              onChange={(e) => setInfo(e.target.value)}
              rows={10}
              className="mt-1.5 rounded-xl border-[#E2DFD8]"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B60]">
              Guide des types de séance (aide Chloé à choisir le bon rendez-vous Calendly)
            </label>
            <Textarea
              data-testid="types-notes-input"
              value={typesNotes}
              onChange={(e) => setTypesNotes(e.target.value)}
              rows={8}
              className="mt-1.5 rounded-xl border-[#E2DFD8]"
            />
          </div>
        </div>
      </div>

      <Button
        data-testid="save-settings-button"
        onClick={save}
        disabled={saving}
        className="rounded-xl bg-[#24643D] px-8 text-white hover:bg-[#1B4A2E]"
      >
        {saving ? "Enregistrement…" : "Enregistrer les paramètres"}
      </Button>

      <div className="rounded-2xl border border-[#E2DFD8] bg-white p-8">
        <h3 className="text-lg font-semibold text-[#1E2A22]">Notifications sur cet appareil</h3>
        <p className="mt-2 text-sm leading-relaxed text-[#5C6B60]">
          Recevez une notification quand un patient demande à vous parler ou vous écrit pendant
          une prise de main. Sur iPhone/iPad : installez d'abord l'app admin sur l'écran d'accueil
          (Partager → « Sur l'écran d'accueil »), puis ouvrez-la et activez les notifications ici
          (iOS 16.4 minimum).
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button
            data-testid="push-toggle-button"
            variant="outline"
            onClick={togglePush}
            disabled={pushBusy}
            className={`rounded-xl ${
              pushEnabled
                ? "border-[#E07A5F] text-[#E07A5F] hover:bg-[#E07A5F] hover:text-white"
                : "border-[#24643D] text-[#24643D] hover:bg-[#24643D] hover:text-white"
            }`}
          >
            {pushBusy ? "…" : pushEnabled ? "Désactiver les notifications" : "Activer les notifications"}
          </Button>
          {pushEnabled && (
            <Button
              data-testid="push-test-button"
              variant="outline"
              onClick={testPush}
              className="rounded-xl border-[#E2DFD8] text-[#5C6B60]"
            >
              Envoyer une notification de test
            </Button>
          )}
          {pushSupport === false && (
            <span className="text-xs text-[#E07A5F]">
              Notifications non disponibles dans ce navigateur (sur iPhone, l'app doit être installée sur l'écran d'accueil).
            </span>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-[#E2DFD8] bg-white p-8">
        <h3 className="text-lg font-semibold text-[#1E2A22]">Session</h3>
        <p className="mt-2 text-sm text-[#5C6B60]">
          {email ? <>Connecté en tant que <strong>{email}</strong>.</> : "Session administrateur active."}
        </p>
        <Button
          data-testid="logout-button"
          variant="outline"
          onClick={onLogout}
          className="mt-4 rounded-xl border-[#E07A5F] text-[#E07A5F] hover:bg-[#E07A5F] hover:text-white"
        >
          <SignOut size={16} className="mr-1" /> Se déconnecter
        </Button>
      </div>
    </div>
  );
};

const LoginScreen = ({ denied }) => (
  <div className="flex min-h-screen items-center justify-center bg-[#F9F8F6] p-6">
    <div className="w-full max-w-md rounded-2xl border border-[#E2DFD8] bg-white p-10 text-center">
      <img
        src={LOGO_URL}
        alt="Logo Cabinet Alésia Thérapie"
        className="mx-auto h-20 w-20 rounded-full border border-[#E2DFD8] bg-white object-contain p-1"
      />
      <h1 className="font-serif-display mt-6 text-3xl font-semibold tracking-tight text-[#1E2A22]">
        Administration
      </h1>
      <p className="mt-2 text-sm text-[#5C6B60]">
        Espace réservé au Cabinet Alésia Thérapie. Connectez-vous avec votre compte Google autorisé.
      </p>
      {denied && (
        <p data-testid="auth-denied-message" className="mt-4 rounded-xl bg-[#E07A5F]/10 px-4 py-3 text-sm text-[#E07A5F]">
          Ce compte Google n'est pas autorisé à accéder à cette administration.
        </p>
      )}
      <Button
        data-testid="google-login-button"
        onClick={() => {
          window.location.href = `${API}/auth/login`;
        }}
        className="mt-6 w-full rounded-xl bg-[#24643D] py-6 text-white hover:bg-[#1B4A2E]"
      >
        <SignIn size={18} className="mr-2" /> Se connecter avec Google
      </Button>
    </div>
  </div>
);

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [auth, setAuth] = useState({ loading: true, authenticated: false, dev_mode: false, email: null });
  const denied = new URLSearchParams(window.location.search).get("auth") === "denied";

  useEffect(() => {
    document.title = "Administration · Cabinet Alésia Thérapie";
    const manifest = document.querySelector('link[rel="manifest"]');
    const touchIcon = document.querySelector('link[rel="apple-touch-icon"]');
    const appTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    const prev = {
      manifest: manifest?.getAttribute("href"),
      icon: touchIcon?.getAttribute("href"),
      title: appTitle?.getAttribute("content"),
    };
    manifest?.setAttribute("href", "/manifest-admin.json");
    touchIcon?.setAttribute("href", "/apple-touch-icon-admin.png");
    appTitle?.setAttribute("content", "Admin Chloé");
    return () => {
      document.title = "Chloé · Cabinet Alésia Thérapie";
      if (prev.manifest) manifest?.setAttribute("href", prev.manifest);
      if (prev.icon) touchIcon?.setAttribute("href", prev.icon);
      if (prev.title) appTitle?.setAttribute("content", prev.title);
    };
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`)
      .then((r) => setAuth({ loading: false, ...r.data }))
      .catch(() => setAuth({ loading: false, authenticated: false }));
  }, []);

  const statsRef = useRef(stats);
  statsRef.current = stats;

  useEffect(() => {
    const clearBadge = () => {
      if ("clearAppBadge" in navigator) navigator.clearAppBadge().catch(() => {});
    };
    const setBadge = () => {
      if (!("setAppBadge" in navigator)) return;
      const s = statsRef.current;
      const n = (s?.relays_new || 0) + (s?.email_drafts_pending || 0);
      if (n > 0) navigator.setAppBadge(n).catch(() => {});
      else clearBadge();
    };
    clearBadge();
    const onVis = () => {
      if (document.visibilityState === "visible") clearBadge();
      else setBadge();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", setBadge);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", setBadge);
    };
  }, []);

  useEffect(() => {
    if (!auth.authenticated) return;
    const loadStats = () => axios.get(`${API}/admin/stats`).then((r) => setStats(r.data)).catch(() => {});
    loadStats();
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") loadStats();
    }, 30000);
    return () => clearInterval(iv);
  }, [tab, auth.authenticated]);

  const [convToOpen, setConvToOpen] = useState(null);
  const openConversation = (sessionId) => {
    setConvToOpen(sessionId);
    setTab("conversations");
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    window.location.reload();
  };

  if (auth.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F9F8F6]">
        <p className="text-sm text-[#5C6B60]">Chargement…</p>
      </div>
    );
  }

  if (!auth.authenticated) {
    return <LoginScreen denied={denied} />;
  }

  const navItems = [
    { id: "overview", label: "Aperçu", icon: ChartBar },
    { id: "conversations", label: "Conversations", icon: ChatsCircle },
    { id: "relays", label: "Messages transmis", icon: EnvelopeSimple, count: stats?.relays_new },
    { id: "emails", label: "E-mails Chloé", icon: Tray, count: stats?.email_drafts_pending },
    { id: "settings", label: "Paramètres", icon: GearSix },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-[#F9F8F6] md:flex-row">
      <aside className="flex w-full shrink-0 flex-col border-b border-[#E2DFD8] bg-[#EFECE6] p-4 md:min-h-screen md:w-64 md:border-b-0 md:border-r md:p-6">
        <div className="mb-4 flex items-center gap-3 md:mb-10">
          <img
            src={LOGO_URL}
            alt="Logo Cabinet Alésia Thérapie"
            data-testid="admin-logo"
            className="h-11 w-11 shrink-0 rounded-full border border-[#E2DFD8] bg-white object-contain p-0.5"
          />
          <div>
            <p className="font-serif-display text-lg font-semibold leading-none text-[#1E2A22]">
              Alésia Thérapie
            </p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#5C6B60]">Administration</p>
          </div>
        </div>
        <nav className="flex gap-1 overflow-x-auto pb-1 md:flex-col md:overflow-visible md:pb-0">
          {navItems.map(({ id, label, icon: Icon, count }) => (
            <button
              key={id}
              data-testid={`nav-${id}`}
              onClick={() => setTab(id)}
              className={`flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-3 py-2 text-sm font-medium transition-colors md:w-full md:gap-3 md:px-4 md:py-3 ${
                tab === id
                  ? "bg-[#24643D] text-white"
                  : "text-[#1E2A22] hover:bg-white/70"
              }`}
            >
              <Icon size={18} weight={tab === id ? "fill" : "regular"} />
              {label}
              {count > 0 && (
                <span
                  data-testid={`nav-${id}-count`}
                  className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                    tab === id ? "bg-white/20 text-white" : "bg-[#E07A5F] text-white"
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          ))}
        </nav>
        <a
          href="/"
          data-testid="nav-patient-chat"
          className="mt-auto hidden items-center gap-2 rounded-xl px-4 py-3 text-sm text-[#5C6B60] transition-colors hover:bg-white/70 md:flex"
        >
          <ArrowSquareOut size={16} /> Vue patient
        </a>
      </aside>

      <main className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-10">
        {tab === "overview" && <Overview stats={stats} />}
        {tab === "conversations" && <Conversations key={convToOpen || "default"} initialSelected={convToOpen} />}
        {tab === "relays" && (
          <RelayedMessages
            onCountChange={(n) => setStats((s) => (s ? { ...s, relays_new: n } : s))}
            onOpenConversation={openConversation}
          />
        )}
        {tab === "emails" && (
          <EmailDrafts
            onCountChange={(n) => setStats((s) => (s ? { ...s, email_drafts_pending: n } : s))}
            onOpenConversation={openConversation}
          />
        )}
        {tab === "settings" && <Settings onLogout={logout} email={auth.email} />}
      </main>
    </div>
  );
}
